import React, { useState, useRef, useEffect } from "react";
import axios from "../../../api/axiosInstance";
import toast from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";

const SectionCard = ({ title, data, fields, onUpdated }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const firstInputRef = useRef(null);

  useEffect(() => {
    if (isEditing) {
      const initData = {};
      fields.forEach((field) => {
        initData[field.name] = data[field.name] || "";
      });
      setFormData(initData);
      setTimeout(() => firstInputRef.current?.focus(), 100);
    }
  }, [isEditing, data, fields]);

  const handleChange = (e) => {
    const { name, value, type } = e.target;

    let newValue = value;
    if (type === "number") {
      newValue = value === "" ? "" : Math.max(0, Number(value));
    }

    setFormData((prev) => ({ ...prev, [name]: newValue }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      };

      const filteredData = {};
      fields.forEach((field) => {
        filteredData[field.name] = formData[field.name];
      });

      const response = await axios.patch(
        "/api/matrimony-profile/update/",
        filteredData,
        config
      );
      toast.success(`${title} updated successfully!`);
      onUpdated(response.data);
      setIsEditing(false);
    } catch (error) {
      console.error(error.response?.data || error.message);
      // toast.error(`Failed to update ${title}.`);
    }
  };

  return (
    <motion.div
      layout
      className="bg-white shadow rounded p-4 mb-4"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: "spring", stiffness: 300, damping: 30 }}
    >
      <div className="flex justify-between items-center mb-2">
        <h2 className="text-lg font-semibold">{title}</h2>
        {!isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className="text-pink-600 hover:underline text-sm"
          >
            Edit
          </button>
        )}
      </div>

      <AnimatePresence initial={false}>
        {!isEditing && (
          <motion.div
            key="display"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-700">
              {fields.map((field) => (
                <div key={field.name}>
                  <span className="font-medium">{field.label}:</span>{" "}
                  {data[field.name] || "Not specified"}
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {isEditing && (
          <motion.form
            key="edit"
            onSubmit={handleSubmit}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="space-y-3 overflow-hidden"
          >
            {fields.map((field, idx) => (
              <div key={field.name}>
                <label className="block text-sm font-medium mb-1">
                  {field.label}
                </label>
                <input
                  ref={idx === 0 ? firstInputRef : null}
                  type={field.type || "text"}
                  name={field.name}
                  value={formData[field.name]}
                  onChange={handleChange}
                  min={field.type === "number" ? 0 : undefined}
                  className="border border-gray-300 p-2 w-full rounded focus:outline-none focus:ring-2 focus:ring-pink-500"
                />
              </div>
            ))}
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setIsEditing(false)}
                className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="bg-pink-600 hover:bg-pink-700 text-white px-4 py-2 rounded"
              >
                Save
              </button>
            </div>
          </motion.form>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default SectionCard;
